/*
 * vp-quote.js — VINYLPAC sticker price engine, ported from Python
 * (vp_console/sticker_quote.py + pricing_book.py + bros_calculator.py) so the
 * Shopify theme and a checkout Worker price stickers with no Python server.
 *
 *   VPQuote.load(chartsObject)   // vp-charts.json, built by build_charts.py
 *   VPQuote.quote(body)          // === serve.py quote_from_body(body)
 *
 * Browser: <script src="vp-quote.js"> -> window.VPQuote.
 * Node / Worker: require('./vp-quote.js') or `import VPQuote from './vp-quote.js'`.
 *
 * Output is the same JSON object the Python returns (keys, order, values,
 * rounding, error objects). Python semantics are emulated where JS differs:
 * round()/format with half-even on the exact binary value, float() string
 * parsing, str()/truthiness of JSON values, str.strip() whitespace, and
 * log/exp bit-for-bit at the chart nodes (see build_charts.py "libm").
 * parity_test.py proves it against the live Python.
 */
(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module && module.exports) module.exports = api;
  if (root) root.VPQuote = api;
})(typeof globalThis !== 'undefined' ? globalThis : (typeof self !== 'undefined' ? self : this), function () {
  'use strict';

  // ── Python number helpers ───────────────────────────────────────────────
  var _f64 = new Float64Array(1);
  var _u64 = new BigUint64Array(_f64.buffer);

  // |x| = m * 2^e exactly (x finite, nonzero).
  function decompose(x) {
    _f64[0] = x;
    var bits = _u64[0];
    var neg = (bits >> 63n) === 1n;
    var ex = Number((bits >> 52n) & 0x7ffn);
    var frac = bits & 0xfffffffffffffn;
    if (ex === 0) return { neg: neg, m: frac, e: -1074 };
    return { neg: neg, m: frac | 0x10000000000000n, e: ex - 1075 };
  }

  function signbit(x) { return x < 0 || Object.is(x, -0); }

  var POW10 = [];
  function pow10n(n) { return POW10[n] || (POW10[n] = 10n ** BigInt(n)); }

  // round(|x| * 10^n) half-even on the exact binary value (CPython dtoa mode 3).
  function scaledDigits(x, n) {
    var d = decompose(x);
    if (d.e >= 0) return { neg: d.neg, q: (d.m << BigInt(d.e)) * pow10n(n) };
    var num = d.m * pow10n(n);
    var den = 1n << BigInt(-d.e);
    var q = num / den;
    var r2 = (num - q * den) * 2n;
    if (r2 > den || (r2 === den && (q & 1n) === 1n)) q += 1n;
    return { neg: d.neg, q: q };
  }

  // Python round(x, n) for float x, n >= 0.
  function pyRound(x, n) {
    if (!isFinite(x) || x === 0) return x;
    var d = decompose(x);
    if (d.e >= 0) return x;
    var s = scaledDigits(x, n);
    return Number((s.neg ? '-' : '') + s.q.toString() + 'e-' + n);
  }

  // Python f"{x:.nf}".
  function pyFixed(x, n) {
    if (x !== x) return 'nan';
    if (x === Infinity) return 'inf';
    if (x === -Infinity) return '-inf';
    var neg = signbit(x);
    var digits = x === 0 ? '0' : scaledDigits(x, n).q.toString();
    if (n > 0) {
      while (digits.length <= n) digits = '0' + digits;
      digits = digits.slice(0, digits.length - n) + '.' + digits.slice(digits.length - n);
    }
    return (neg ? '-' : '') + digits;
  }

  // Python repr(float).
  function pyReprFloat(x) {
    if (x !== x) return 'nan';
    if (x === Infinity) return 'inf';
    if (x === -Infinity) return '-inf';
    if (x === 0) return signbit(x) ? '-0.0' : '0.0';
    var neg = x < 0;
    var ex = Math.abs(x).toExponential(); // shortest round-trip digits
    var parts = ex.split('e');
    var digits = parts[0].replace('.', '');
    var exp10 = parseInt(parts[1], 10);
    var decpt = exp10 + 1;
    var out;
    if (decpt > -4 && decpt <= 16) {
      if (decpt <= 0) out = '0.' + '0'.repeat(-decpt) + digits;
      else if (decpt >= digits.length) out = digits + '0'.repeat(decpt - digits.length) + '.0';
      else out = digits.slice(0, decpt) + '.' + digits.slice(decpt);
    } else {
      var mant = digits.length > 1 ? digits[0] + '.' + digits.slice(1) : digits;
      var e = exp10 < 0 ? '-' : '+';
      var ae = Math.abs(exp10);
      out = mant + 'e' + e + (ae < 10 ? '0' + ae : String(ae));
    }
    return (neg ? '-' : '') + out;
  }

  // Python str(int) for an integer-valued double.
  function pyIntStr(n) {
    return Math.abs(n) < 1e21 ? String(n === 0 ? 0 : n) : BigInt(n).toString();
  }

  // ── Python string / value helpers ───────────────────────────────────────
  var PY_SPACE = null;     // Set of code points (str.isspace)
  var PY_DIGITS = null;    // [[start, end], ...] decimal-digit runs

  function pyStrip(s) {
    var cps = Array.from(s);
    var a = 0, b = cps.length;
    while (a < b && PY_SPACE.has(cps[a].codePointAt(0))) a++;
    while (b > a && PY_SPACE.has(cps[b - 1].codePointAt(0))) b--;
    return cps.slice(a, b).join('');
  }

  function pyLowerStrip(s) { return pyStrip(String(s).toLowerCase()); }

  // A JSON float literal whose value is whole ("2.0", "1e2"). Python keeps it a
  // float (str() -> "2.0"); a JS number cannot. Only quoteJSON produces these.
  function PyFloat(v) { this.v = v; }

  function pyTruthy(v) {
    if (v === undefined || v === null) return false;
    if (v instanceof PyFloat) return v.v !== 0;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v !== 0;
    if (typeof v === 'string') return v.length > 0;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return Object.keys(v).length > 0;
    return true;
  }

  function pyReprStr(s) {
    var q = s.indexOf("'") !== -1 && s.indexOf('"') === -1 ? '"' : "'";
    var out = '';
    for (var ch of s) {
      var c = ch.codePointAt(0);
      if (ch === '\\') out += '\\\\';
      else if (ch === q) out += '\\' + q;
      else if (ch === '\n') out += '\\n';
      else if (ch === '\r') out += '\\r';
      else if (ch === '\t') out += '\\t';
      else if (c < 0x20 || c === 0x7f) out += '\\x' + c.toString(16).padStart(2, '0');
      else out += ch;
    }
    return q + out + q;
  }

  // Python str(value) for a JSON value. JSON numbers that are whole are taken as
  // Python ints (json.loads gives int for "2", float for "2.0"; JS cannot tell).
  function pyStr(v, nested) {
    if (v === undefined || v === null) return 'None';
    if (v === true) return 'True';
    if (v === false) return 'False';
    if (v instanceof PyFloat) return pyReprFloat(v.v);
    if (typeof v === 'number') return Number.isInteger(v) ? pyIntStr(v) : pyReprFloat(v);
    if (typeof v === 'string') return nested ? pyReprStr(v) : v;
    if (Array.isArray(v)) return '[' + v.map(function (x) { return pyStr(x, true); }).join(', ') + ']';
    if (typeof v === 'object') {
      return '{' + Object.keys(v).map(function (k) { return pyReprStr(k) + ': ' + pyStr(v[k], true); }).join(', ') + '}';
    }
    return String(v);
  }

  function pyDigitValue(c) {
    if (c >= 48 && c <= 57) return c - 48;
    for (var i = 0; i < PY_DIGITS.length; i++) {
      var r = PY_DIGITS[i];
      if (c < r[0]) return -1;
      if (c <= r[1]) return (c - r[0]) % 10;
    }
    return -1;
  }

  var FLOAT_RE = /^[+-]?(?:(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?|inf|infinity|nan)$/i;

  // Python float(value); NaN where Python raises (callers treat both as invalid).
  function pyFloat(v) {
    if (v instanceof PyFloat) return v.v;
    if (typeof v === 'number') return v;
    if (typeof v === 'boolean') return v ? 1 : 0;
    if (typeof v !== 'string') return NaN;
    // _PyUnicode_TransformDecimalAndSpaceToASCII
    var t = '';
    for (var ch of v) {
      var c = ch.codePointAt(0);
      if (c < 128) { t += ch; continue; } // ASCII kept; only \t\n\v\f\r and space strip below
      if (PY_SPACE.has(c)) { t += ' '; continue; }
      var d = pyDigitValue(c);
      t += d >= 0 ? String(d) : '?';
    }
    t = t.replace(/^[ \t\n\v\f\r]+|[ \t\n\v\f\r]+$/g, '');
    if (t.indexOf('_') !== -1) {
      for (var i = 0; i < t.length; i++) {
        if (t[i] !== '_') continue;
        if (!(i > 0 && /[0-9]/.test(t[i - 1]) && i + 1 < t.length && /[0-9]/.test(t[i + 1]))) return NaN;
      }
      t = t.replace(/_/g, '');
    }
    if (!FLOAT_RE.test(t)) return NaN;
    var lower = t.toLowerCase().replace(/^[+-]/, '');
    if (lower === 'inf' || lower === 'infinity') return t[0] === '-' ? -Infinity : Infinity;
    if (lower === 'nan') return NaN;
    return Number(t);
  }

  // ── correctly rounded log / exp (BigInt fixed point) + Apple-libm overrides ──
  var P = 200n;
  var ONE = 1n << P;
  function bitLength(a) { return a === 0n ? 0 : a.toString(16).length * 4 - (Math.clz32(parseInt(a.toString(16)[0], 16)) - 28); }
  function isqrt(n) {
    var x = 1n << BigInt(Math.ceil(bitLength(n) / 2));
    for (;;) { var y = (x + n / x) >> 1n; if (y >= x) return x; x = y; }
  }
  var LN2 = (function () { // 2*atanh(1/3), 16 guard bits
    var G = P + 16n, one = 1n << G, sum = 0n, term = one / 3n, n = 1n;
    while (term !== 0n) { sum += term / n; term /= 9n; n += 2n; }
    return (2n * sum) >> 16n;
  })();
  var SQRT2 = isqrt(2n << (2n * P));

  var POW2 = new Float64Array(2100);
  for (var i2 = 0; i2 < 2100; i2++) POW2[i2] = Math.pow(2, i2 - 1074);
  function pow2(n) { return POW2[n + 1074]; }

  // nearest double to R * 2^-p (R BigInt).
  function fixedToDouble(R, p) {
    if (R === 0n) return 0;
    var neg = R < 0n, a = neg ? -R : R;
    var bl = bitLength(a), q = a, sh = 0;
    if (bl > 53) {
      sh = bl - 53;
      var bsh = BigInt(sh);
      q = a >> bsh;
      var rem = a - (q << bsh), half = 1n << (bsh - 1n);
      if (rem > half || (rem === half && (q & 1n) === 1n)) q += 1n;
    }
    var e = sh - p;
    var v = Number(q);
    if (e >= -1074 && e <= 1023) v *= pow2(e);
    else { var e1 = Math.trunc(e / 2); v = v * pow2(e1) * pow2(e - e1); }
    return neg ? -v : v;
  }

  function crLog(x) {
    if (x === 1) return 0;
    if (!(x > 0) || !isFinite(x)) return Math.log(x);
    var d = decompose(x);
    var bl = bitLength(d.m);
    var k = d.e + bl - 1;
    var F = d.m << (P - BigInt(bl - 1));
    if (F > SQRT2) { F >>= 1n; k += 1; }
    var s = ((F - ONE) << P) / (F + ONE);
    var s2 = (s * s) >> P; // s*s >= 0
    var sum = 0n, term = s, n = 1n;
    while (term !== 0n) { sum += term / n; term = (term * s2) / ONE; n += 2n; } // truncates toward 0
    return fixedToDouble(BigInt(k) * LN2 + 2n * sum, Number(P));
  }

  function floorDiv(a, b) { var q = a / b; if ((a % b !== 0n) && ((a < 0n) !== (b < 0n))) q -= 1n; return q; }

  function crExp(y) {
    if (y === 0) return 1;
    if (!isFinite(y) || Math.abs(y) > 700) return Math.exp(y);
    var d = decompose(y);
    var sh = d.e + Number(P);
    var Y = sh >= 0 ? d.m << BigInt(sh) : d.m >> BigInt(-sh);
    if (d.neg) Y = -Y;
    var k = floorDiv(2n * Y + LN2, 2n * LN2);
    var r = Y - k * LN2;
    var sum = ONE, term = ONE, n = 1n;
    for (;;) { term = ((term * r) / ONE) / n; if (term === 0n) break; sum += term; n += 1n; }
    return fixedToDouble(sum, Number(P) - Number(k));
  }

  var LOG_OVR = new Map(), EXP_OVR = new Map();
  function pyLog(x) { var o = LOG_OVR.get(x); return o !== undefined ? o : crLog(x); }
  function pyExp(y) { var o = EXP_OVR.get(y); return o !== undefined ? o : crExp(y); }

  // ── engine state ────────────────────────────────────────────────────────
  var C = null; // loaded charts
  var K = null; // derived constants

  function hasOwn(o, k) { return Object.prototype.hasOwnProperty.call(o, k); }
  function get(o, k) { return o && hasOwn(o, k) ? o[k] : undefined; }

  function load(charts) {
    if (typeof charts === 'string') charts = JSON.parse(charts);
    if (!charts || charts.format !== 1) throw new Error('vp-charts.json: unsupported format');
    var c = charts.const;
    LOG_OVR = new Map(); EXP_OVR = new Map();
    Object.keys(charts.libm.log).forEach(function (k) { LOG_OVR.set(Number(k), charts.libm.log[k]); });
    Object.keys(charts.libm.exp).forEach(function (k) { EXP_OVR.set(Number(k), charts.libm.exp[k]); });
    PY_SPACE = new Set(charts.py.space);
    PY_DIGITS = charts.py.digits;
    var mats = charts.materials.map(function (m) {
      var byArea = new Map();
      m.areas.forEach(function (a, i) { byArea.set(a, m.log_p[i]); });
      return { name: m.name, areas: m.areas, qtys: m.qtys, log_a: m.log_a, log_q: m.log_q, log_p: byArea, discounts: m.discounts };
    });
    var books = {};
    Object.keys(charts.books).forEach(function (file) {
      var cells = charts.books[file], keys = Object.keys(cells), hs = new Set(), ws = new Set();
      keys.forEach(function (k) {
        var p = k.split('x');
        hs.add(parseInt(p[0], 10)); ws.add(parseInt(p[1], 10));
      });
      var num = function (a, b) { return a - b; };
      books[file] = { cells: cells, keys: keys, hs: Array.from(hs).sort(num), ws: Array.from(ws).sort(num) };
    });
    K = {
      TIERS: c.TIERS, KISS_INCHES: c.KISS_INCHES, FINISHES: c.FINISHES, FINISH_ALIASES: c.FINISH_ALIASES,
      CUTS: c.CUTS, CUTS_WITH_EMBOSS: new Set(c.CUTS_WITH_EMBOSS), EMBOSSES: c.EMBOSSES,
      CUT_ALIASES: c.CUT_ALIASES, EMBOSS_ALIASES: c.EMBOSS_ALIASES, BROS_EMBOSS: c.BROS_EMBOSS,
      BROS_MATERIAL: c.BROS_MATERIAL, BROS_SPECIALTY: new Set(c.BROS_SPECIALTY),
      BROS_QTY_KNOTS: new Set(c.BROS_QTY_KNOTS), KISS_CHART: c.KISS_CHART,
      HOLO_VINYL: new Set(c.HOLO_VINYL), REFLECTIVE_VINYL: new Set(c.REFLECTIVE_VINYL),
      SPOT_VINYL: new Set(c.SPOT_VINYL),
      GLOSS_MATTE: new Set(['gloss', 'matte', 'semi-gloss', 'semi gloss', 'semigloss', 'satin']),
      calc: charts.calc, books: books, mats: mats, muleArea: charts.mule_area || {}
    };
    C = charts;
    return api;
  }

  function annotate(result) {
    var meta = C ? C.pricing_book : null;
    var out = Object.assign({}, result);
    out.pricing_book_hash = meta ? meta.effective_hash : null;
    out.pricing_book = meta ? {
      ok: meta.ok, effective_hash: meta.effective_hash, loaded_at: meta.loaded_at,
      candidate_hash: meta.candidate_hash, error: meta.error,
      dependencies: Object.assign({}, meta.dependencies)
    } : { ok: false, effective_hash: null, loaded_at: null, candidate_hash: null, error: 'pricing book unavailable', dependencies: {} };
    return out;
  }

  // ── bros_calculator.py ──────────────────────────────────────────────────
  function cents(x) { return pyRound(x + 1e-12, 2); }

  function extraMultiplier(emboss, finish) {
    var add = K.calc.EMBOSS[emboss] + K.calc.FINISH[finish];
    return add > 0 ? 1 + add : 1.0;
  }

  function vpFromBros(unit) { return cents(unit); }

  function applyVpUnit(unit, emboss) {
    var add = hasOwn(K.calc.SHOP_OVER_BROS, emboss) ? K.calc.SHOP_OVER_BROS[emboss] : 0.0;
    return cents(unit + add);
  }

  function lerpB(x, x0, x1, y0, y1) { return x1 === x0 ? y0 : y0 + (y1 - y0) * (x - x0) / (x1 - x0); }

  function priceFromTable(mat, w, h, qty) {
    var areas = mat.areas, logA = mat.log_a, logQ = mat.log_q, qtys = mat.qtys;
    var area = w * h;
    var la = pyLog(area), lq = pyLog(qty);
    la = Math.min(Math.max(la, logA[0]), logA[logA.length - 1]);
    lq = Math.min(Math.max(lq, logQ[0]), logQ[logQ.length - 1]);
    var ai = 0, i;
    for (i = 0; i < logA.length - 1; i++) {
      if (logA[i] <= la && la <= logA[i + 1]) { ai = i; break; }
      ai = i;
    }
    var qi = 0, j;
    for (j = 0; j < logQ.length - 1; j++) {
      if (logQ[j] <= lq && lq <= logQ[j + 1]) { qi = j; break; }
      qi = j;
    }
    var a0 = areas[ai], a1 = areas[Math.min(ai + 1, areas.length - 1)];
    var q0 = qi, q1 = Math.min(qi + 1, logQ.length - 1);
    var p0 = mat.log_p.get(a0), p1 = mat.log_p.get(a1);
    var lp0 = lerpB(lq, logQ[q0], logQ[q1], p0[q0], p0[q1]);
    var lp1 = lerpB(lq, logQ[q0], logQ[q1], p1[q0], p1[q1]);
    var lp = lerpB(la, logA[ai], logA[Math.min(ai + 1, logA.length - 1)], lp0, lp1);
    var table = pyExp(lp);
    if (qty < qtys[0]) table = table / qtys[0] * qty;
    return table;
  }

  function pyFloatOr0(v) { return pyTruthy(v) ? Number(v) : 0; }

  function calculate(mat, w, h, qty, extra) {
    var table = priceFromTable(mat, w, h, qty);
    var ds = mat.discounts.slice().sort(function (a, b) { return pyFloatOr0(b.minQty) - pyFloatOr0(a.minQty); });
    var disc = 0;
    for (var i = 0; i < ds.length; i++) {
      if (qty >= pyFloatOr0(ds[i].minQty)) { disc = pyFloatOr0(ds[i].discount); break; }
    }
    var total = table * (1 - disc / 100) * extra;
    var unit = total / qty;
    return { bros_unit: pyRound(unit, 4) };
  }

  function matName(mat) { return pyStrip(mat.name); }

  function specialtyFilmDelta(spec, white, w, h, qty, extra) {
    var s = vpFromBros(calculate(spec, w, h, qty, extra).bros_unit);
    var wv = vpFromBros(calculate(white, w, h, qty, extra).bros_unit);
    var c = cents(s - wv);
    return c > 0.0 ? c : 0.0; // Python max(0.0, c)
  }

  function applySpecialtyUpcharge(unit, spec, white, w, h, qty, emboss, finish) {
    if (matName(spec) === 'White Vinyl') return unit;
    var extraSame = extraMultiplier(emboss, finish);
    var delta = specialtyFilmDelta(spec, white, w, h, qty, extraSame);
    var whiteFinish = finish === 'matte' ? 'gloss' : finish;
    var extraWhite = extraMultiplier(emboss, whiteFinish);
    var whiteRaw = vpFromBros(calculate(white, w, h, qty, extraWhite).bros_unit);
    var whiteBilled = applyVpUnit(whiteRaw, emboss);
    var b = whiteBilled + delta;
    return cents(b > unit ? b : unit);
  }

  // ── sticker_quote.py ────────────────────────────────────────────────────
  function money(n) { var v = Number(n); return pyRound((v > 0.0 ? v : 0.0) + 1e-9, 2); }

  function inch(n) {
    var s = pyFixed(Number(n), 2).replace(/0+$/, '').replace(/\.$/, '');
    return s || '0';
  }

  function roundInch(x) {
    var n = Number(x);
    var whole = Math.trunc(n);
    var frac = n - whole;
    var out = frac <= 0.5 ? whole : whole + 1;
    if (n > 0 && out < 1) out = 1;
    return Math.max(1, Math.min(9, out));
  }

  function qtyKey(qty, kiss) {
    if (kiss) return '10';
    var chosen = 10, q = Math.trunc(qty);
    for (var i = 0; i < K.TIERS.length; i++) {
      if (q >= K.TIERS[i]) chosen = K.TIERS[i]; else break;
    }
    return String(chosen);
  }

  function lerp(a, b, t) { return a + (b - a) * t; }

  // Python (q - a) / (b - a) with q, a, b ints.
  function intSpanT(q, a, b) {
    if (Math.abs(q) <= 9007199254740992) return (q - a) / (b - a);
    return bigSpan(q, a, b);
  }
  function bigSpan(q, a, b) {
    var N = BigInt(q) - BigInt(a), D = BigInt(b - a);
    var s = Math.max(0, 60 + bitLength(D) - bitLength(N < 0n ? -N : N));
    var neg = N < 0n;
    if (neg) N = -N;
    var num = N << BigInt(s), qq = num / D, sticky = num % D !== 0n;
    var sh = bitLength(qq) - 53;
    if (sh > 0) {
      var bsh = BigInt(sh), low = qq & ((1n << bsh) - 1n), half = 1n << (bsh - 1n);
      qq >>= bsh;
      if (low > half || (low === half && (sticky || (qq & 1n) === 1n))) qq += 1n;
    } else sh = 0;
    var v = Number(qq) * pow2(sh - s);
    return neg ? -v : v;
  }

  function axisSpan(x, xs) {
    if (!xs.length) throw new Error('empty size grid');
    if (xs.length === 1) return [xs[0], xs[0], 0.0];
    if (x <= xs[0]) return [xs[0], xs[1], 0.0];
    var n = xs.length;
    if (x >= xs[n - 1]) { var a = xs[n - 2], b = xs[n - 1]; return [a, b, (x - a) / (b - a)]; }
    for (var i = 0; i + 1 < n; i++) {
      var a2 = xs[i], b2 = xs[i + 1];
      if (a2 <= x && x <= b2) return b2 === a2 ? [a2, b2, 0.0] : [a2, b2, (x - a2) / (b2 - a2)];
    }
    var a3 = xs[n - 2], b3 = xs[n - 1];
    return [a3, b3, (x - a3) / (b3 - a3)];
  }

  function cellUnits(cell, holo) { return holo && cell.hu ? cell.hu : cell.u; }

  function qtyUnit(units, qty) {
    var q = Math.trunc(qty), key;
    if (q < 200) { key = qtyKey(q); return [units[key], key]; }
    var keys = Object.keys(units).map(function (k) { return parseInt(k, 10); })
      .filter(function (k) { return k >= 200; }).sort(function (a, b) { return a - b; });
    if (!keys.length) { key = qtyKey(q); return [units[key], key]; }
    var qs = pyIntStr(q);
    if (hasOwn(units, qs)) return [units[qs], qs];
    if (q <= keys[0]) { key = String(keys[0]); return [units[key], key]; }
    var n = keys.length;
    // Past the last key the price holds (stretching the last slope sent 40k+ labels to a penny; Josh 10/13).
    if (q >= keys[n - 1]) { key = String(keys[n - 1]); return [units[key], key]; }
    for (var i = 0; i + 1 < n; i++) {
      var a2 = keys[i], b2 = keys[i + 1];
      if (a2 <= q && q <= b2) {
        if (q === a2) return [units[a2], String(a2)];
        return [lerp(units[a2], units[b2], intSpanT(q, a2, b2)), a2 + '-' + b2];
      }
    }
    key = qtyKey(q);
    return [units[key], key];
  }

  function cornerUnit(cells, h, w, qty, holo) {
    var k = h + 'x' + w;
    if (!hasOwn(cells, k)) return null;
    return qtyUnit(cellUnits(cells[k], holo), qty);
  }

  function lamTables() { return C.addons; }

  function laminateAdd(finish) {
    if (!pyTruthy(finish)) return [0.0, null];
    var raw = pyLowerStrip(finish);
    if (raw.indexOf('vinyl') !== -1) return [0.0, null];
    var ad = lamTables();
    var sur = ad.laminate_surcharge || {}, aliases = ad.laminate_alias || {};
    var key = hasOwn(aliases, raw) ? aliases[raw] : raw.split(' ').join('-');
    key = hasOwn(aliases, key) ? aliases[key] : key;
    if (!hasOwn(sur, key)) return [0.0, null];
    return [money(Number(sur[key])), key];
  }

  // Taken before the 200+ round-up (two rounded-up prices can step their gap up with qty; issue 29).
  function dieHoloDelta(h, w, qty) {
    var white = smUnitRaw('diecut-stickers.json', h, w, qty, false)[0];
    var holoU = smUnitRaw('diecut-stickers.json', h, w, qty, true)[0];
    var d = holoU - white;
    var delta = money(d > 0.0 ? d : 0.0);
    return [delta, 'holo via die delta +$' + pyFixed(delta, 2)];
  }

  function reflectiveAdd(qty, white, holoUnit) {
    var rs = C.addons.reflective_surcharge;
    var add = money(Number(pyTruthy(rs) ? rs : 0.80));
    var out = money(white + add);
    var note = 'reflective +$' + pyFixed(add, 2) + ' (not blended)';
    if (holoUnit !== null && out <= Number(holoUnit) + 1e-9) {
      out = money(Number(holoUnit) + 0.10);
      note = 'reflective ≥ holo +$0.10 → $' + pyFixed(out, 2);
    }
    return [out, note];
  }

  function isReflectiveFinish(finish) {
    if (!pyTruthy(finish)) return false;
    var f = pyLowerStrip(finish);
    return K.REFLECTIVE_VINYL.has(f) || f === 'reflective';
  }

  function isHoloVinyl(finish) {
    if (!pyTruthy(finish)) return false;
    if (laminateAdd(finish)[1]) return false;
    var f = pyLowerStrip(finish);
    if (f.indexOf('spot') !== -1) return false;
    return K.HOLO_VINYL.has(f);
  }

  function kissVinylChart(finish) {
    if (!pyTruthy(finish)) return null;
    var f = pyLowerStrip(finish);
    if (laminateAdd(finish)[1]) return null;
    if (K.SPOT_VINYL.has(f)) return 'spot-holo-reflective';
    if (K.REFLECTIVE_VINYL.has(f)) return 'full-reflective';
    if (isHoloVinyl(finish)) return 'full-holographic';
    if (K.GLOSS_MATTE.has(f)) return 'gloss-matte';
    return null;
  }

  function cellKey(h, w, cells) {
    var key = h + 'x' + w;
    if (hasOwn(cells, key)) return [key, false];
    var best = null, bestD = null;
    Object.keys(cells).forEach(function (k) {
      var p = k.split('x');
      if (p.length !== 2) return;
      var hh = parseInt(p[0], 10), ww = parseInt(p[1], 10);
      var d = Math.abs(hh - h) + Math.abs(ww - w);
      if (bestD === null || d < bestD) { best = k; bestD = d; }
    });
    return [best || key, true];
  }

  function normCut(cut, kind) {
    var v = pyTruthy(cut) ? cut : (pyTruthy(kind) ? kind : 'tape');
    var raw = pyLowerStrip(pyStr(v));
    return hasOwn(K.CUT_ALIASES, raw) ? K.CUT_ALIASES[raw] : 'tape';
  }

  function normFinish(finish) {
    var raw = pyLowerStrip(pyStr(pyTruthy(finish) ? finish : 'gloss'));
    var key = hasOwn(K.FINISH_ALIASES, raw) ? K.FINISH_ALIASES[raw] : raw;
    if (hasOwn(K.FINISHES, key)) return K.FINISHES[key];
    var lamKey = laminateAdd(raw)[1];
    if (lamKey) return { id: lamKey, label: C.addons.laminate_label[lamKey], hint: 'laminate' };
    return K.FINISHES.gloss;
  }

  function normEmboss(emboss) {
    var key;
    if (typeof emboss === 'boolean') key = emboss ? 'std' : 'none';
    else {
      var raw = pyLowerStrip(pyStr(pyTruthy(emboss) ? emboss : 'none'));
      key = hasOwn(K.EMBOSS_ALIASES, raw) ? K.EMBOSS_ALIASES[raw] : 'none';
    }
    return hasOwn(K.EMBOSSES, key) ? K.EMBOSSES[key] : K.EMBOSSES.none;
  }

  function kissUnit(h, w, finish, finishId) {
    var flags = [];
    var extra = 0.0;
    var la = laminateAdd(finish), lam = la[0], lamKey = la[1];
    var chartName = kissVinylChart(finish) || (hasOwn(K.KISS_CHART, finishId) ? K.KISS_CHART[finishId] : null);
    if (lamKey) {
      chartName = 'gloss-matte';
      extra = lam;
      flags.push('laminate ' + lamKey + ' +$' + pyFixed(lam, 2));
    } else if (!chartName) {
      chartName = 'gloss-matte';
    }
    var grid = C.kisscut[chartName];
    var hi = K.KISS_INCHES.indexOf(h) !== -1 ? K.KISS_INCHES.indexOf(h) : Math.min(h, 9) - 1;
    var wi = K.KISS_INCHES.indexOf(w) !== -1 ? K.KISS_INCHES.indexOf(w) : Math.min(w, 9) - 1;
    hi = Math.max(0, Math.min(grid.length - 1, hi));
    wi = Math.max(0, Math.min(grid[hi].length - 1, wi));
    return [Number(grid[hi][wi]) + extra, 'kisscut.json ' + chartName + ' ' + h + 'x' + w, flags];
  }

  // Big stickers (Josh 10/19): kiss + tape, die and plain kiss go up to 28" short side x 84" long. Past 9" on the long
  // side they cost at least material x 4 (wide-format print + laminate per consumed sq in, 1/8" gap all round), scaled
  // by the finish (tape: its own 9x9 cell over gloss's; die/kiss: the 10-piece price over plain gloss's, so the floor is
  // the same at every qty and more never costs more each) -- "rather be overpriced".
  var OVERSIZE_CUTS = new Set(['tape', 'die', 'kiss']);
  var OVERSIZE_MAX_SHORT = 28.0, OVERSIZE_MAX_LONG = 84.0, OVERSIZE_MATERIAL_SQIN = 0.0247, OVERSIZE_MARKUP = 4.0,
    OVERSIZE_GAP = 0.25;
  // Kiss + tape bulk (Josh 10/31): flat to 200, then tape x kiss@qty / kiss@200; never under plain kiss-cut.
  var TAPE_BULK_FROM = 200;
  // Roll labels top out at 9" on either side (Josh 10/31).
  var ROLL_MAX_SIDE = 9.0;

  function tapeOversize(width, height, finish, finishId) {
    return oversize(width, height, kissUnit(9, 9, finish, finishId)[0], kissUnit(9, 9, 'gloss', 'gloss')[0]);
  }

  function oversize(width, height, unit, glossUnit) {
    var material = OVERSIZE_MATERIAL_SQIN * (width + OVERSIZE_GAP) * (height + OVERSIZE_GAP);
    return OVERSIZE_MARKUP * material * unit / glossUnit;
  }

  function rep2(s) { return s.split('  ').join(' '); }

  // 200+ rounds UP to the cent so an in-between size or qty never lands under Mule (Josh 10/13).
  function upCent(x) { return Math.ceil(pyRound(Number(x) * 100, 6)) / 100; }

  function muleQtyUnit(t, ks, qty) {
    var q = Math.trunc(qty), m = ks.length;
    if (q <= ks[0]) return t[0] / ks[0];
    if (q >= ks[m - 1]) return t[m - 1] / ks[m - 1];
    for (var j = 0; j + 1 < m; j++) {
      if (ks[j] <= q && q <= ks[j + 1]) return lerp(t[j] / ks[j], t[j + 1] / ks[j + 1], intSpanT(q, ks[j], ks[j + 1]));
    }
    return t[m - 1] / ks[m - 1];
  }

  // Mule's own price each for this area (Mule prices by area alone): at the next harvested area up, or with
  // `between` straight-line between the two harvested areas around it (inside the grid).
  function muleAreaUnit(file, area, qty, holo, between) {
    var entry = K.muleArea[file], table = entry && entry[holo ? 'holo_areas' : 'areas'];
    if (!table) return null;
    var i = 0, n = table.a.length;
    while (i < n - 1 && !(table.a[i] >= area - 1e-9)) i++;
    var hi = muleQtyUnit(table.t[i], table.q, qty);
    if (!between || i === 0 || table.a[i] <= area) return hi;
    var lo = muleQtyUnit(table.t[i - 1], table.q, qty);
    return lerp(lo, hi, (area - table.a[i - 1]) / (table.a[i] - table.a[i - 1]));
  }

  function smUnit(file, h, w, qty, holo) {
    var r = smUnitRaw(file, h, w, qty, holo);
    if (Math.trunc(qty) >= 200) r[0] = upCent(r[0]);
    return r;
  }

  // Before the round-up; past the chart's largest size, the chart rule on Mule's own price for the area (issue 29).
  function smUnitRaw(file, h, w, qty, holo) {
    var r = smUnitLerp(file, h, w, qty, holo);
    if (Math.trunc(qty) < 200) return r;
    var hh = Math.max(0.5, Math.min(24.0, Number(h))), ww = Math.max(0.5, Math.min(24.0, Number(w)));
    var book = K.books[file], hs = book.hs, ws = book.ws;
    var add = K.muleArea[file] ? (K.muleArea[file].add || 0) : 0, mule;
    if (hh > hs[hs.length - 1] || ww > ws[ws.length - 1]) {
      // a side under the grid's smallest size counts as that size, and past the edge never beats the edge price
      mule = muleAreaUnit(file, Math.max(hh, hs[0]) * Math.max(ww, ws[0]), qty, holo, false);
      if (mule !== null) {
        var edge = smUnitRaw(file, Math.min(hh, hs[hs.length - 1]), Math.min(ww, ws[ws.length - 1]), qty, holo)[0];
        r[0] = Math.max(Math.max(0.05, mule) + add, edge); r[1] = r[1] + ' + Mule area price';
      }
    } else {
      mule = muleAreaUnit(file, hh * ww, qty, holo, true);
      if (mule !== null && Math.max(0.05, mule) + add > r[0]) { r[0] = Math.max(0.05, mule) + add; r[1] = r[1] + ' \u2265 Mule area'; }
    }
    return r;
  }

  function smUnitLerp(file, h, w, qty, holo) {
    var flags = [];
    var book = K.books[file], cells = book.cells;
    var hh = Math.max(0.5, Math.min(24.0, Number(h)));
    var ww = Math.max(0.5, Math.min(24.0, Number(w)));
    var hs = axisSpan(hh, book.hs), wsp = axisSpan(ww, book.ws);
    var h0 = hs[0], h1 = hs[1], th = hs[2], w0 = wsp[0], w1 = wsp[1], tw = wsp[2];
    var hi = Math.abs(th - 1.0) < 1e-12 ? h1 : (Math.abs(th) < 1e-12 ? h0 : null);
    var wi = Math.abs(tw - 1.0) < 1e-12 ? w1 : (Math.abs(tw) < 1e-12 ? w0 : null);
    var onCell = hi !== null && wi !== null && hasOwn(cells, hi + 'x' + wi);
    var corners = [
      [h0, w0, 1.0 - th, 1.0 - tw],
      [h1, w0, th, 1.0 - tw],
      [h0, w1, 1.0 - th, tw],
      [h1, w1, th, tw]
    ];
    var got, unit, qsrc, src;
    if (onCell) {
      got = cornerUnit(cells, hi, wi, qty, holo);
      unit = got[0]; qsrc = got[1];
      src = rep2(file + ' ' + hi + 'x' + wi + ' ' + (holo ? 'holo ' : '') + qsrc);
      if (holo && !cells[hi + 'x' + wi].hu) flags.push(file + ' has no holo cell; white ' + hi + 'x' + wi);
      return [unit, src, flags];
    }
    var acc = 0.0, weight = 0.0;
    qsrc = qtyKey(qty);
    for (var i = 0; i < 4; i++) {
      var c = corners[i];
      got = cornerUnit(cells, c[0], c[1], qty, holo);
      var wt = c[2] * c[3];
      if (got === null || wt === 0) continue;
      qsrc = got[1];
      acc += got[0] * wt;
      weight += wt;
    }
    if (weight <= 0) {
      var ck = cellKey(roundInch(hh), roundInch(ww), cells);
      if (ck[1]) flags.push('nearest size ' + pyReprFloat(hh) + 'x' + pyReprFloat(ww) + ' → ' + ck[0]);
      got = qtyUnit(cellUnits(cells[ck[0]], holo), qty);
      src = rep2(file + ' ' + ck[0] + ' ' + (holo ? 'holo ' : '') + got[1]);
      return [got[0], src, flags];
    }
    unit = acc / weight;
    var kind = (th < -1e-12 || th > 1 + 1e-12 || tw < -1e-12 || tw > 1 + 1e-12) ? 'extrap' : 'interp';
    flags.push(kind + ' ' + inch(hh) + 'x' + inch(ww) + ' from ' + h0 + 'x' + w0 + '/' + h1 + 'x' + w1 +
      (String(qsrc).indexOf('-') !== -1 ? ' q ' + qsrc : ''));
    if (holo) {
      var sk = h0 + 'x' + w0;
      var sample = hasOwn(cells, sk) ? cells[sk] : cells[book.keys[0]];
      if (!sample.hu) flags.push(file + ' has no holo cell; white interp');
      src = file + ' ' + kind + ' ' + inch(hh) + 'x' + inch(ww) + ' holo ' + qsrc;
    } else {
      src = file + ' ' + kind + ' ' + inch(hh) + 'x' + inch(ww) + ' ' + qsrc;
    }
    return [unit, src, flags];
  }

  function brosMaterialRow(name) {
    var want = pyStrip(name || '');
    var mats = K.mats, i;
    for (i = 0; i < mats.length; i++) if (pyStrip(mats[i].name) === want) return mats[i];
    var lowered = want.toLowerCase();
    for (i = 0; i < mats.length; i++) if (lowered && mats[i].name.toLowerCase().indexOf(lowered) !== -1) return mats[i];
    return mats[0];
  }

  function brosComboForFinish(finishId, embossId) {
    var material = hasOwn(K.BROS_MATERIAL, finishId) ? K.BROS_MATERIAL[finishId] : 'White Vinyl';
    var tier = K.BROS_EMBOSS[embossId];
    var lam = finishId === 'matte' || K.BROS_SPECIALTY.has(finishId) ? 'matte' : 'gloss';
    return [material, tier + '/' + lam];
  }

  function brosEmbossUnit(h, w, qty, material, combo) {
    var flags = [];
    var hh = Math.max(0.5, Math.min(24.0, Number(h)));
    var ww = Math.max(0.5, Math.min(24.0, Number(w)));
    var q = Math.max(1, Math.trunc(qty));
    var cut = combo.indexOf('/');
    var tier = combo.slice(0, cut), lam = combo.slice(cut + 1);
    var extra = extraMultiplier(tier, lam);
    var mat = brosMaterialRow(material);
    var white = brosMaterialRow('White Vinyl');
    var name = matName(mat);
    if (name !== pyStrip(material || '')) flags.push('material ' + material + ' → ' + name);

    function bill(n) {
      var r = calculate(mat, ww, hh, n, extra);
      var raw = vpFromBros(r.bros_unit);
      var floored = applyVpUnit(raw, tier);
      return applySpecialtyUpcharge(floored, mat, white, ww, hh, n, tier, lam);
    }

    // Josh 10/14: 50-100 = 10% under Bros; 100->200 slides straight down to the 200 price.
    function under(n) {
      var over = hasOwn(K.calc.SHOP_OVER_BROS, tier) ? K.calc.SHOP_OVER_BROS[tier] : 0.0;
      return cents(0.9 * (bill(n) - over));
    }
    function small(n) {
      var rule = n <= 100 ? under(n) : cents(lerp(under(100), bill(200), (n - 100) / 100));
      var at200 = bill(200);
      return rule > at200 ? rule : at200; // big sizes: Bros barely drops 100->200
    }

    var film = 0.0;
    if (name !== 'White Vinyl') film = specialtyFilmDelta(mat, white, ww, hh, Math.max(q, 50), extra);
    var unit, src;
    if (q < 50) {
      unit = small(50);
      if (film > 0) flags.push('specialty film +$' + pyFixed(film, 2) + ' on white ' + tier + '/gloss');
      var c = cents(unit + 0.25);
      unit = c > 1.00 ? c : 1.00;
      src = 'embossed-diecut.json ' + inch(hh) + 'x' + inch(ww) + ' ' + name + ' ' + combo + ' 10';
      flags.push('10-49 = 50-tier + $0.25 from Bros packet');
      if (name !== 'White Vinyl' && lam === 'matte') flags.push('specialty uses Bros matte UV (no gloss varnish extra)');
      return [unit, src, flags];
    }
    unit = q < 200 ? small(q) : bill(q);
    src = 'embossed-diecut.json ' + inch(hh) + 'x' + inch(ww) + ' ' + name + ' ' + combo + ' ' + pyIntStr(q);
    if (q < 200) flags.push('50-199 = 10% under Bros');
    if (!K.BROS_QTY_KNOTS.has(q)) flags.push('bros packet qty ' + pyIntStr(q));
    if (film > 0) flags.push('specialty film +$' + pyFixed(film, 2) + ' on white ' + tier + '/gloss');
    if (name !== 'White Vinyl' && lam === 'matte') flags.push('specialty uses Bros matte UV (no gloss varnish extra)');
    return [unit, src, flags];
  }

  function invalid(error, flag) {
    return { ok: false, needs_inference: true, error: error, flags: [flag], each: null, total: null };
  }

  function quoteInner(w, h, qty, finish, kind, cut, emboss, base, laminate) {
    var width = pyFloat(w), height = pyFloat(h), quantity = pyFloat(qty);
    var valid = !(typeof w === 'boolean' || typeof h === 'boolean' || typeof qty === 'boolean');
    valid = valid && [width, height, quantity].every(function (v) { return isFinite(v) && v > 0; });
    valid = valid && Number.isInteger(quantity);
    if (!valid) return invalid('invalid pricing inputs', 'finite positive dimensions and whole quantity required');
    var cutId = normCut(cut, kind);
    if (OVERSIZE_CUTS.has(cutId) && (Math.min(width, height) > OVERSIZE_MAX_SHORT || Math.max(width, height) > OVERSIZE_MAX_LONG)) {
      return invalid(cutId === 'tape' ? 'unsupported kisscut size' : 'unsupported size', 'tops out at 28 x 84 inches');
    }
    if (cutId === 'roll' && Math.max(width, height) > ROLL_MAX_SIDE) {
      return invalid('unsupported size', 'roll labels top out at 9 x 9 inches');
    }
    // real* = the size billed; the die/kiss/sheet/roll charts still read it clamped to 24".
    var cap = OVERSIZE_CUTS.has(cutId) ? OVERSIZE_MAX_LONG : 24.0;
    var realW = Math.max(0.5, Math.min(cap, width));
    var realH = Math.max(0.5, Math.min(cap, height));
    cap = cutId === 'tape' ? OVERSIZE_MAX_LONG : 24.0;
    width = Math.max(0.5, Math.min(cap, width));
    height = Math.max(0.5, Math.min(cap, height));
    var count = quantity;
    var fin = normFinish(finish);
    var cutMeta = K.CUTS[cutId];
    var raised = normEmboss(emboss);
    if (cutId === 'tape' || cutId === 'roll') raised = K.EMBOSSES.none;
    var rh = roundInch(height), rw = roundInch(width);
    var flags = [], notes = [], src = '', unit, got;

    var la = laminateAdd(finish), lam = la[0], lamKey = la[1];
    if (!lamKey) { la = laminateAdd(fin.id); lam = la[0]; lamKey = la[1]; }
    var holo = isHoloVinyl(finish) || (fin.id === 'holo' && !lamKey);
    var wantRef = isReflectiveFinish(finish) || isReflectiveFinish(fin.id);

    function overlay(u, fl) {
      if (lamKey && !holo) {
        u += lam;
        fl.push('laminate ' + lamKey + ' +$' + pyFixed(lam, 2) + ' (not blended)');
      }
      return u;
    }

    function labelHolo(file) {
      var r = smUnit(file, height, width, count, false);
      var u = r[0], s = r[1], fl = r[2];
      if (holo) {
        var dd = dieHoloDelta(height, width, count);
        u = money(u + dd[0]);
        fl = fl.concat([dd[1]]);
        s = s + ' + holo delta';
      }
      return [u, s, fl];
    }

    var holoUnit = null, white;
    if (cutId === 'tape') {
      got = kissUnit(rh, rw, finish, fin.id);
      if (count > TAPE_BULK_FROM) {
        var lt = laminateAdd(finish), addT = lt[1] ? lt[0] : 0.0;
        var kissLadder = function (n) {   // plain kiss-cut's price for this vinyl, as cut "kiss" prices it
          var u = smUnit('kisscut-mule.json', height, width, n, holo)[0];
          if (wantRef) u = reflectiveAdd(n, u, smUnit('kisscut-mule.json', height, width, n, true)[0])[0];
          return u;
        };
        var kAt = kissLadder(count), kFrom = kissLadder(TAPE_BULK_FROM);
        if (kAt > 0 && kAt < kFrom) {
          got[0] = (got[0] - addT) * kAt / kFrom + addT;
          got[1] = got[1] + ' x kiss ' + pyIntStr(count) + '/' + TAPE_BULK_FROM;
          got[2].push('kiss + tape past ' + TAPE_BULK_FROM + ' = tape x the plain kiss-cut ladder');
        }
      }
      if (Math.max(width, height) > 9) {
        var bigTape = tapeOversize(width, height, finish, fin.id);
        if (bigTape > got[0]) { got[0] = bigTape; got[1] = got[1] + ' edge -> material x4 past 9 inches'; }
        got[2].push('kiss + tape past 9 inches = material x4');
      }
      var kissQ = quoteInner(width, height, count, finish, 'kiss', 'kiss', false, false);
      if (kissQ.ok && kissQ.each > got[0]) {
        got[0] = kissQ.each; got[1] = got[1] + ' -> plain kiss-cut floor';
        got[2].push('kiss + tape never under plain kiss-cut');
      }
    } else if (raised.id !== 'none' && K.CUTS_WITH_EMBOSS.has(cutId)) {
      var combo = brosComboForFinish(fin.id, raised.id);
      got = brosEmbossUnit(height, width, count, combo[0], combo[1]);
      wantRef = false;
    } else if (cutId === 'roll') {
      got = labelHolo('labels-rolls.json');
      got[0] = overlay(got[0], got[2]);
      if (wantRef) {
        white = smUnit('labels-rolls.json', height, width, count, false)[0];
        holoUnit = money(white + dieHoloDelta(height, width, count)[0]);
      }
    } else if (cutId === 'kiss') {
      got = smUnit('kisscut-mule.json', height, width, count, holo);
      got[0] = overlay(got[0], got[2]);
      if (wantRef) holoUnit = smUnit('kisscut-mule.json', height, width, count, true)[0];
    } else if (cutId === 'sheet') {
      got = labelHolo('labels-sheets.json');
      got[0] = overlay(got[0], got[2]);
      if (wantRef) {
        white = smUnit('labels-sheets.json', height, width, count, false)[0];
        holoUnit = money(white + dieHoloDelta(height, width, count)[0]);
      }
    } else {
      got = smUnit('diecut-stickers.json', height, width, count, holo);
      got[0] = overlay(got[0], got[2]);
      if (wantRef) holoUnit = smUnit('diecut-stickers.json', height, width, count, true)[0];
    }
    unit = got[0]; src = got[1]; flags = got[2];

    if (wantRef && cutId !== 'tape' && raised.id === 'none') {
      var file = cutId === 'sheet' ? 'labels-sheets.json' : cutId === 'roll' ? 'labels-rolls.json'
        : cutId === 'kiss' ? 'kisscut-mule.json' : 'diecut-stickers.json';
      white = smUnit(file, height, width, count, false)[0];
      var ra = reflectiveAdd(count, white, holoUnit);
      unit = ra[0];
      flags.push(ra[1]);
    }

    if ((cutId === 'die' || cutId === 'kiss') && Math.max(realW, realH) > 9 && !base) {
      var ten = quoteInner(width, height, 10, finish, cutId, cutId, emboss, true);
      var plain = quoteInner(width, height, 10, 'gloss', cutId, cutId, false, true);
      var big = oversize(realW, realH, ten.each, plain.each);
      if (big > unit) { unit = big; src = src + ' -> material x4 past 9 inches'; }
      flags.push(cutId + ' past 9 inches = material x4');
    }

    if (cutId === 'tape' && count > TAPE_BULK_FROM) {
      notes.push('Weeding + tape: flat to 200, then it drops with the plain kiss-cut ladder.');
    }
    if (count < 10) notes.push('MOQ 10 per design. This qty uses the 10-piece rate.');

    // Josh 10/31 ("Charge the laminate add"): a laminate on a vinyl = the vinyl's price + its flat add; never on emboss.
    var lx = laminate ? laminateAdd(laminate) : [0.0, null], lamExtraKey = lx[1];
    if (lamExtraKey && raised.id === 'none') {
      unit += lx[0];
      flags.push('laminate ' + lamExtraKey + ' +$' + pyFixed(lx[0], 2) + ' on the vinyl');
    } else {
      lamExtraKey = null;
    }

    if (!isFinite(unit) || unit <= 0 || !isFinite(unit * count)) {
      return invalid('invalid calculated price', 'review quantity and dimensions');
    }
    var each = money(unit);
    var total = money(each * count);
    var bits = [pyIntStr(count) + ' × ' + cutMeta.label.toLowerCase() + ' ' + inch(realW) + '×' + inch(realH), fin.label];
    if (raised.id !== 'none') bits.push('emboss ' + raised.label.toLowerCase());
    if (lamExtraKey) bits.push('+ ' + lamExtraKey);
    return {
      ok: true,
      w: realW,
      h: realH,
      qty: count,
      cut: cutId,
      cut_label: cutMeta.label,
      kind: cutId,
      emboss: raised.id,
      emboss_label: raised.label,
      finish: fin.id,
      finish_label: fin.label,
      sqin: pyRound(realW * realH, 2),
      setup: 0,
      each: each,
      total: total,
      cell: src,
      flags: flags,
      notes: notes,
      rounded: rh + 'x' + rw,
      line: bits.join('  ·  ') + '  ·  $' + pyFixed(total, 2) + '  ·  $' + pyFixed(each, 2) + ' ea'
    };
  }

  // sticker_quote.quote(w, h, qty, finish, kind, cut, emboss), with its
  // @pricing_book.calculated("quote") wrapper.
  function quoteArgs(w, h, qty, finish, kind, cut, emboss, laminate) {
    if (finish === undefined) finish = 'gloss';
    if (kind === undefined) kind = 'tape';
    if (cut === undefined) cut = null;
    if (emboss === undefined) emboss = false;
    if (!C) {
      return annotate({
        ok: false, needs_inference: true, status: 'needs_inference', error: 'pricing_book_invalid',
        pricing_book_error: 'pricing book unavailable', flags: ['pricing book invalid — review required'],
        line_items: [], each: null, total: null
      });
    }
    return annotate(quoteInner(w, h, qty, finish, kind, cut, emboss, false, laminate));
  }

  // serve.py quote_from_body(body).
  function quote(body) {
    if (!body || typeof body !== 'object' || Array.isArray(body)) body = {};
    var or = function () {
      for (var i = 0; i < arguments.length; i++) if (pyTruthy(arguments[i])) return arguments[i];
      return arguments[arguments.length - 1];
    };
    try {
      return quoteArgs(
        or(get(body, 'w'), 3),
        or(get(body, 'h'), 3),
        or(get(body, 'qty'), 50),
        pyStr(or(get(body, 'finish'), 'gloss')),
        pyStr(or(get(body, 'kind'), get(body, 'cut'), 'tape')),
        pyStr(or(get(body, 'cut'), get(body, 'kind'), 'tape')),
        hasOwn(body, 'emboss') ? (body.emboss === undefined ? null : body.emboss) : false,
        pyStr(or(get(body, 'laminate'), ''))
      );
    } catch (e) {
      return { ok: false, error: String(e && e.message || e) };
    }
  }

  // ── Python json.loads (strict) ──────────────────────────────────────────
  function pyJsonLoads(s) {
    var i = 0, n = s.length;
    function fail() { throw new SyntaxError('JSONDecodeError'); }
    function ws() { while (i < n && (s[i] === ' ' || s[i] === '\t' || s[i] === '\n' || s[i] === '\r')) i++; }
    var NUM = /-?(?:0|[1-9]\d*)(\.\d+)?([eE][-+]?\d+)?/y;
    function str() {
      i++; // opening quote
      var out = '';
      for (;;) {
        if (i >= n) fail();
        var c = s.charCodeAt(i);
        if (c === 34) { i++; return out; }
        if (c < 0x20) fail();
        if (c !== 92) { out += s[i++]; continue; }
        var e = s[i + 1];
        i += 2;
        if (e === '"') out += '"'; else if (e === '\\') out += '\\'; else if (e === '/') out += '/';
        else if (e === 'b') out += '\b'; else if (e === 'f') out += '\f'; else if (e === 'n') out += '\n';
        else if (e === 'r') out += '\r'; else if (e === 't') out += '\t';
        else if (e === 'u') {
          var hex = s.substr(i, 4);
          if (!/^[0-9a-fA-F]{4}$/.test(hex)) fail();
          var u = parseInt(hex, 16);
          i += 4;
          if (u >= 0xd800 && u <= 0xdbff && s[i] === '\\' && s[i + 1] === 'u') {
            var hex2 = s.substr(i + 2, 4);
            if (/^[0-9a-fA-F]{4}$/.test(hex2)) {
              var u2 = parseInt(hex2, 16);
              if (u2 >= 0xdc00 && u2 <= 0xdfff) { out += String.fromCharCode(u, u2); i += 6; continue; }
            }
          }
          out += String.fromCharCode(u);
        } else fail();
      }
    }
    function value() {
      if (i >= n) fail();
      var c = s[i];
      if (c === '"') return str();
      if (c === '{') {
        i++; ws();
        var obj = {};
        if (s[i] === '}') { i++; return obj; }
        for (;;) {
          if (s[i] !== '"') fail();
          var k = str(); ws();
          if (s[i] !== ':') fail();
          i++; ws();
          var v = value();
          if (k === '__proto__') Object.defineProperty(obj, k, { value: v, enumerable: true, writable: true, configurable: true });
          else obj[k] = v;
          ws();
          if (s[i] === '}') { i++; return obj; }
          if (s[i] !== ',') fail();
          i++; ws();
        }
      }
      if (c === '[') {
        i++; ws();
        var arr = [];
        if (s[i] === ']') { i++; return arr; }
        for (;;) {
          arr.push(value()); ws();
          if (s[i] === ']') { i++; return arr; }
          if (s[i] !== ',') fail();
          i++; ws();
        }
      }
      if (s.startsWith('null', i)) { i += 4; return null; }
      if (s.startsWith('true', i)) { i += 4; return true; }
      if (s.startsWith('false', i)) { i += 5; return false; }
      if (s.startsWith('NaN', i)) { i += 3; return NaN; }
      if (s.startsWith('Infinity', i)) { i += 8; return Infinity; }
      if (s.startsWith('-Infinity', i)) { i += 9; return -Infinity; }
      NUM.lastIndex = i;
      var m = NUM.exec(s);
      if (!m) fail();
      i += m[0].length;
      var num = Number(m[0]);
      if ((m[1] || m[2]) && Number.isInteger(num)) return new PyFloat(num);
      return num;
    }
    if (s.charCodeAt(0) === 0xfeff) fail();
    ws();
    var result = value();
    ws();
    if (i !== n) fail();
    return result;
  }

  // serve.py POST /api/quote: body = json.loads(text or '{}'), {} if that fails.
  function quoteJSON(text) {
    var body;
    try { body = pyJsonLoads(text ? String(text) : '{}'); } catch (e) { body = {}; }
    return quote(body);
  }

  var api = {
    load: load,
    quote: quote,
    quoteJSON: quoteJSON,
    quoteArgs: quoteArgs,
    get loaded() { return !!C; },
    get pricingBookHash() { return C ? C.pricing_book.effective_hash : null; },
    _py: { round: pyRound, fixed: pyFixed, repr: pyReprFloat, float: pyFloat, log: crLog, exp: crExp, pyLog: pyLog, pyExp: pyExp }
  };
  return api;
});
